import { faServer } from "@fortawesome/pro-light-svg-icons"
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  Typography
} from "@mui/material"
import Grid from "@mui/material/Grid"
import TextField from "@mui/material/TextField"
import { DatePicker } from "@mui/x-date-pickers"
import { useApiGet } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import { default as Button, default as SubmitButton } from "components/Button"
import NonFormikSidebar from "components/sidedrawer/NonFormikSidebar"
import { StyledStickyButtonBox } from "components/sidedrawer/Sidebar.styled"
import SidebarHeader from "components/sidedrawer/SidebarHeader"
import theme from "css/theme/light"
import { Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { t } from "i18next"
import moment from "moment"
import { equipmentToDeviceValidationSchema } from "pages/systemadmin/pages/devices/components/validationSchema"
import { useContext, useEffect, useRef, useState } from "react"

export const SidebarEquipmentForm = ({ onSubmit, result }) => {
  const initialValues = {
    equipmentId: "",
    companyId: 0,
    status: "OK",
    equipmentTypeId: null,
    subscriptionTime: null,
    equipmentDescription: "",
    mountedOnDeviceId: null,
    mountedOnStorageId: null,
    hasConnectToDevice: false,

    supplements: []
  }

  //const [supplement, setNewSupplement] = useState({ alarmType: null, avlId: null })

  const [externalDeviceId, setExternalDeviceId] = useState("")

  const [companyResult, getCompany] = useApiGet()

  const [externalDeviceResult, getExternalDevice] = useApiGet()
  const [externalConfigsResult, getExternalConfigs] = useApiGet()

  useEffect(() => {
    getCompany(apiEndpoints.systemadmindeviceregistry.listcompanies)
    getExternalDevice(apiEndpoints.systemadmindeviceregistry.listexternaldeviceregistrydevicetypes)
  }, [getCompany, getExternalDevice])

  useEffect(() => {
    if (externalDeviceId !== "") {
      getExternalConfigs(
        apiEndpoints.systemadmindeviceregistry.listexternaldeviceregistryconfigurations.replace(
          "{deviceTypeId}",
          externalDeviceId
        )
      )
    }
  }, [externalDeviceId, getExternalConfigs])

  const appContext = useContext(AppContext)
  const formRef = useRef()

  const handleSubmit = async (values) => {
    onSubmit(values)
  }

  useEffect(() => {
    if (result && result.status === 2 && formRef.current) {
      formRef.current.resetForm()
      appContext.toggleSideBar("equipment", false)
    }
  }, [result])

  // const addSupplement = (setFieldValue, values) => {
  //   if (supplement.alarmType !== null && supplement.avlId !== null) {
  //     let newSupplements = values.supplements
  //     newSupplements.push({ alarmType: supplement.alarmType, avlId: supplement.avlId })
  //     setFieldValue("supplements", newSupplements)
  //     setNewSupplement({ alarmType: null, avlId: undefined })
  //   }
  // }

  // const removeSupplement = (setFieldValue, values, index) => {
  //   let newSupplements = values.supplements
  //   newSupplements.splice(index, 1)
  //   setFieldValue("supplements", newSupplements)
  // }
  return (
    <Formik
      innerRef={formRef}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validateOnChange={false}
      validationSchema={equipmentToDeviceValidationSchema}>
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting, handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit} noValidate>
          <NonFormikSidebar
            buttonChildren={
              <StyledStickyButtonBox p={{ xs: 3, sm: 4 }}>
                <SubmitButton
                  result={result}
                  style={{ width: "100%" }}
                  color="primary"
                  type="submit"
                  variant="contained">
                  {t("global.save_changes", "Save changes")}
                </SubmitButton>
                <Button
                  variant="outlined"
                  onClick={() => {
                    appContext.toggleSideBarMulti("equipment", false)
                  }}>
                  {t("global.buttons.cancel", "Cancel")}
                </Button>
              </StyledStickyButtonBox>
            }
            sidebarId="equipment">
            {companyResult.status === 2 && externalDeviceResult.status === 2 && (
              <Box pt={12} pb={5}>
                <SidebarHeader
                  icon={faServer}
                  backgroundColor={theme.palette.primary[300]}
                  color={theme.palette.primary[800]}
                  size="lg"
                  title={t("global.settings", "Settings")}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Autocomplete
                      id="externalDeviceTypeId"
                      options={externalDeviceResult?.data}
                      getOptionLabel={(option) => (option.name ? option.name : "")}
                      value={
                        externalDeviceResult?.data?.find((i) => i.id === values.externalDeviceTypeId) || ""
                      }
                      isOptionEqualToValue={(option, value) => {
                        return option.id === value.id
                      }}
                      onChange={(e, value) => {
                        setFieldValue("externalDeviceTypeId", value?.id)
                        setExternalDeviceId(value?.id || "")
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("global.equipment_type", "Equipment Type")}
                          variant="outlined"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.externalDeviceTypeId && errors.externalDeviceTypeId ? true : false}
                          helperText={
                            touched.externalDeviceTypeId && errors.externalDeviceTypeId
                              ? errors.externalDeviceTypeId
                              : null
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="equipmentId"
                      label={t("global.id", "ID")}
                      value={values.equipmentId ? values.equipmentId : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.equipmentId && errors.equipmentId ? true : false}
                      helperText={touched.equipmentId && errors.equipmentId ? errors.equipmentId : null}
                      variant="outlined"
                    />
                  </Grid>
                  {externalConfigsResult.status === 2 &&
                    (externalConfigsResult.data.length === 1 ? (
                      <Grid item xs={12} sm={12}>
                        <Autocomplete
                          id="externalConfigId"
                          options={externalConfigsResult?.data}
                          getOptionLabel={(option) => (option.name ? option.name : "")}
                          value={
                            externalConfigsResult?.data?.find((i) => i.id === values.externalConfigId) || ""
                          }
                          isOptionEqualToValue={(option, value) => {
                            return option.id === value.id
                          }}
                          onChange={(e, value) => {
                            setFieldValue("externalConfigId", value?.id)
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("global.configuration", "Configuration")}
                              variant="outlined"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.externalConfigId && errors.externalConfigId ? true : false}
                              helperText={
                                touched.externalConfigId && errors.externalConfigId
                                  ? errors.externalConfigId
                                  : null
                              }
                            />
                          )}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Connected configuration</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {externalConfigsResult.data[0]?.name}
                        </Typography>
                      </Grid>
                    ))}
                  {/* <Grid item xs={12} md={12}>
                      {externalDeviceResult.data.findIndex(
                        (i) => i.id === values.equipmentTypeId && i.equipmentTypeName === "Positioning"
                      ) !== -1 && (
                        <Box marginBottom={2}>
                          <Typography variant="h5">{t("global.add_alarm_type", "Add alarm type")}</Typography>
                          <Grid container direction="row" justifyContent="space-between" alignItems="center">
                            <Grid item xs={5} sm={7}>
                              <FormControl>
                                <RadioGroup
                                  row
                                  aria-label="alarmType"
                                  name="alarmType"
                                  value={supplement.alarmType}
                                  onChange={(e) => {
                                    const value =
                                      e.target.value === "" ? null : Number.parseInt(e.target.value)
                                    setNewSupplement({ ...supplement, alarmType: value })
                                  }}>
                                  <FormControlLabel
                                    value={1}
                                    control={<Radio />}
                                    label={t("vehiclealarm.alcohol_lock", "Alcohol lock")}
                                  />
                                  <FormControlLabel
                                    value={2}
                                    control={<Radio />}
                                    label={t("global.immobilizer", "Immobilizer")}
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                            <Grid item xs={5} sm={3}>
                              <FormControl>
                                <TextField
                                  autoComplete="off"
                                  sx={{ background: theme.palette.background.paper }}
                                  fullWidth
                                  id="supplementavlId"
                                  name="supplementavlId"
                                  label={t("global.avl_id", "AVL ID")}
                                  value={values.supplementavlId}
                                  type="number"
                                  placeholder="0 - 85000"
                                  onChange={(e) => {
                                    setNewSupplement({ ...supplement, avlId: e.target.value })
                                  }}
                                  variant="outlined"
                                />
                              </FormControl>
                            </Grid>
                            <Grid item xs={1}>
                              <IconButton
                                onClick={() => addSupplement(setFieldValue, values)}
                                color="primary">
                                <FontAwesomeIcon icon={faPlus} />
                              </IconButton>
                            </Grid>
                          </Grid>

                          {values.supplements.map((data, index) => (
                            <Box
                              gap="2px"
                              borderBottom={values.supplements.length - 1 !== index ? 1 : 0}
                              borderColor={theme.palette.grey.light}
                              mb={values.supplements.length - 1 !== index ? 2 : 0}>
                              <Grid
                                container
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center">
                                <Grid item xs={7}>
                                  {data.alarmType === 2
                                    ? t("global.immobilizer", "Immobilizer")
                                    : t("vehiclealarm.alcohol_lock", "Alcohol lock")}
                                </Grid>
                                <Grid item xs={3}>
                                  {data.avlId}
                                </Grid>
                                <Grid item xs={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      removeSupplement(setFieldValue, values, index)
                                    }}
                                    color="primary">
                                    <FontAwesomeIcon variant="contained" icon={faTrash} />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Grid> */}
                  <Grid item xs={12}>
                    <Divider sx={{ marginY: 3 }} />
                    <Typography variant="h4">
                      {t("global.invoicing_documents", "Invoicing documents")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          onChange={(e) => setFieldValue("ownedByCustomer", e.target.checked)}
                          checked={values.ownedByCustomer}
                        />
                      }
                      label={t(
                        "global.customer _is_the_owner_of_equipment",
                        "Customer is the owner of equipment"
                      )}
                    />{" "}
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      id="companyId"
                      options={companyResult?.data}
                      getOptionLabel={(option) => (option.name ? option.name : "")}
                      value={companyResult?.data?.find((i) => i.id === values.companyId) || ""}
                      isOptionEqualToValue={(option, value) => {
                        return option.id === value.id
                      }}
                      onChange={(e, value) => {
                        setFieldValue("companyId", value?.id)
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("global.company", "Company")}
                          variant="outlined"
                          name="companyId"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.companyId && errors.companyId ? true : false}
                          helperText={touched.companyId && errors.companyId ? errors.companyId : null}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DatePicker
                      fullWidth
                      value={values?.subscriptionTime !== null ? moment(values?.subscriptionTime) : null}
                      onChange={(value) => {
                        setFieldValue("subscriptionTime", value)
                      }}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          {...params}
                          error={touched.subscriptionTime && errors.subscriptionTime ? true : false}
                          helperText={
                            touched.subscriptionTime && errors.subscriptionTime
                              ? errors.subscriptionTime
                              : null
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="equipmentDescription"
                      label={t("global.description", "Description")}
                      value={values.equipmentDescription}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}{" "}
          </NonFormikSidebar>
        </form>
      )}
    </Formik>
  )
}
